import { type BaseDocumentPayload } from '#app/services/ai/types.ts'
import {
	type LegalSourceCitationBehavior,
	type LegalSourceCitationType,
} from '#app/services/legal-ai/shared/source-contract.ts'
import { type SupportedCountry } from '#app/services/legal-ai/shared/types.ts'

export const OFFICIAL_PUBLICATION_SOURCE_IDS = {
	Gaceta: 'cr/gaceta',
	BoletinJudicial: 'cr/boletin-judicial',
} as const

/**
 * Source IDs are registry-owned strings (`country/source`). Keep this open so
 * adding a country adapter does not require changing shared extraction code.
 */
export type OfficialPublicationSourceId = string

export type OfficialPublicationSnapshot = {
	id: string
	country: SupportedCountry
	sourceId: OfficialPublicationSourceId
	date: string
	content: string
	status: string
	externalDocumentId?: string | null
	url?: string | null
}

export type OfficialPublicationVectorPayload = {
	type: 'official-publication'
	country: SupportedCountry
	sourceId: OfficialPublicationSourceId
	sourceLabel: string
	snapshotId: string
	idDocument: string
	externalDocumentId?: string | null
	publicationDate: string
	publicationDateEpochDay: number
	title: string
	chunkIndex: number
	chunkCount: number
	content: string
	contentHash: string
	indexVersion: number
}

export type OfficialPublicationPayload = BaseDocumentPayload & {
	id: string | number
	sourceId: OfficialPublicationSourceId
	country: SupportedCountry
	snapshotId: string
	externalDocumentId?: string | null
	publicationDate: string
	title: string
	citationGroundingText?: string
}

export type OfficialPublicationSourceDefinition = {
	id: OfficialPublicationSourceId
	country: SupportedCountry
	aliases: readonly string[]
	label: string
	timeZone: string
	locale: string
	activation: 'explicit'
	retrievalKind: 'official-publication-date-range'
	citationType: LegalSourceCitationType
	snapshotKind: string
	citation: LegalSourceCitationBehavior<OfficialPublicationPayload>
	prompt: {
		intro: string
		buildChunk: (payload: OfficialPublicationPayload) => string
		responseInstructions?: readonly string[]
	}
	merge: {
		dedupeKey: (payload: Omit<OfficialPublicationPayload, 'content'>) => string | number
		documentKey: (payload: Omit<OfficialPublicationPayload, 'content'>) => string
		chunkKey: (payload: Omit<OfficialPublicationPayload, 'content'>) => string
	}
	buildTitle: (snapshot: OfficialPublicationSnapshot) => string
	buildDocumentId: (snapshot: OfficialPublicationSnapshot) => string
	loadSnapshot: (snapshotId: string) => Promise<OfficialPublicationSnapshot | null>
	listSnapshots: (args: {
		fromDate: string
		toDate: string
		status?: string
		cursor?: string
		limit: number
		includeContent?: boolean
	}) => Promise<OfficialPublicationSnapshot[]>
}
